// SYSTEM_ROUTE: /routines
import { useState } from 'react';
import type {
  Habit,
  HabitSummary,
  Routine,
  RoutineDayType,
  WeekdayMask,
} from '@life-os/app-core';
import { maskForDayType, routineDueToday, routineMinutes } from '@life-os/app-core';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

const HABIT_STATE_TONE: Record<HabitSummary['state'], ChipTone> = {
  on_track: 'primary',
  due_today: 'warn',
  resting: 'muted',
  paused: 'muted',
};

const HABIT_STATE_LABEL: Record<HabitSummary['state'], string> = {
  on_track: 'Done today',
  due_today: 'Due today',
  resting: 'Rest day',
  paused: 'Paused',
};

const DAY_TYPES: RoutineDayType[] = ['everyday', 'weekdays', 'weekends'];

interface HabitRow {
  habit: Habit;
  summary: HabitSummary;
}

function maskLabel(mask: WeekdayMask): string {
  if (mask.every(Boolean)) return 'Every day';
  return DAY_LABELS.filter((_, i) => mask[i]).join(' ');
}

function HabitsPane() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [title, setTitle] = useState('');
  const [mask, setMask] = useState<WeekdayMask>([true, true, true, true, true, true, true]);

  const rows = useAsync<HabitRow[]>(async () => {
    const habits = await services.routines.listHabits();
    return Promise.all(
      habits.map(async (habit) => ({ habit, summary: await services.routines.habitSummary(habit.id) })),
    );
  }, [revision]);

  const toggleDay = (i: number) => {
    setMask((m) => m.map((v, idx) => (idx === i ? !v : v)) as unknown as WeekdayMask);
  };

  const create = async () => {
    if (title.trim().length === 0 || !mask.some(Boolean)) return;
    await services.routines.createHabit({ title, weekdayMask: mask });
    setTitle('');
    setMask([true, true, true, true, true, true, true]);
    refresh();
  };

  const items = rows.value ?? [];

  return (
    <Pane title="Habits" hint={`${items.length} · schedule-based behaviours`}>
      <Stack>
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 220px' }}
            value={title}
            placeholder="e.g. Meditate"
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void create();
            }}
            aria-label="Habit title"
          />
          <div role="group" aria-label="Weekday schedule" style={{ display: 'flex', gap: 4 }}>
            {DAY_LABELS.map((d, i) => (
              <button
                key={i}
                type="button"
                aria-pressed={mask[i]}
                aria-label={`Day ${i}`}
                onClick={() => toggleDay(i)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  border: '1px solid var(--lc-outline-variant)',
                  background: mask[i] ? 'var(--lc-primary)' : 'var(--lc-surface-container)',
                  color: mask[i] ? 'var(--lc-on-primary)' : 'var(--lc-on-surface-variant)',
                  font: 'inherit',
                  fontSize: 'var(--lc-text-sm)',
                }}
              >
                {d}
              </button>
            ))}
          </div>
          <Button variant="primary" onClick={() => void create()}>
            Add habit
          </Button>
        </div>

        {items.length === 0 ? (
          <EmptyState title="No habits yet">
            A habit is one behaviour on a weekly schedule. Pick the days, then check in when you do it.
          </EmptyState>
        ) : (
          items.map(({ habit, summary }) => (
            <CompactRow
              key={String(habit.id)}
              title={habit.title}
              subtitle={`${maskLabel(habit.weekdayMask)} · streak ${summary.currentStreakDays} day${summary.currentStreakDays === 1 ? '' : 's'}`}
              meta={
                <>
                  <Chip tone={HABIT_STATE_TONE[summary.state]}>{HABIT_STATE_LABEL[summary.state]}</Chip>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      void services.routines
                        .setHabitStatus(habit.id, habit.status === 'paused' ? 'active' : 'paused')
                        .then(refresh)
                    }
                  >
                    {habit.status === 'paused' ? 'Resume' : 'Pause'}
                  </Button>
                  <Button
                    variant="primary"
                    disabled={summary.checkedInToday}
                    onClick={() => void services.routines.checkIn(habit.id).then(refresh)}
                  >
                    {summary.checkedInToday ? 'Checked in' : 'Check in'}
                  </Button>
                </>
              }
            />
          ))
        )}
      </Stack>
    </Pane>
  );
}

function RoutinesPane() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const nowIso = new Date().toISOString();
  const [title, setTitle] = useState('');
  const [dayType, setDayType] = useState<RoutineDayType>('everyday');
  const [stepsText, setStepsText] = useState('');

  const rows = useAsync<readonly Routine[]>(() => services.routines.listRoutines(), [revision]);

  const create = async () => {
    if (title.trim().length === 0) return;
    const steps = stepsText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        const m = line.match(/^(.*?)(?:\s*\((\d+)\s*m\))?$/);
        return { label: (m?.[1] ?? line).trim(), minutes: m?.[2] ? Number(m[2]) : null };
      });
    await services.routines.createRoutine({ title, dayType, weekdayMask: maskForDayType(dayType), steps });
    setTitle('');
    setStepsText('');
    refresh();
  };

  const items = rows.value ?? [];

  const selectStyle = {
    background: 'var(--lc-surface-container)',
    color: 'var(--lc-on-surface)',
    border: '1px solid var(--lc-outline-variant)',
    borderRadius: 'var(--lc-radius-full)',
    padding: '6px 12px',
    font: 'inherit',
    fontSize: 'var(--lc-text-sm)',
  } as const;

  return (
    <Pane title="Routines" hint={`${items.length} · ordered step blocks`}>
      <Stack>
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 200px' }}
            value={title}
            placeholder="e.g. Morning routine"
            onChange={(e) => setTitle(e.target.value)}
            aria-label="Routine title"
          />
          <select value={dayType} onChange={(e) => setDayType(e.target.value as RoutineDayType)} aria-label="Day type" style={selectStyle}>
            {DAY_TYPES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <textarea
            className="lc-cmdbar"
            style={{ flex: '1 1 240px', minHeight: 64, resize: 'vertical' }}
            value={stepsText}
            placeholder={'Steps, one per line\nStretch (5m)\nJournal (10m)'}
            onChange={(e) => setStepsText(e.target.value)}
            aria-label="Routine steps"
          />
          <Button variant="primary" onClick={() => void create()}>
            Add routine
          </Button>
        </div>

        {items.length === 0 ? (
          <EmptyState title="No routines yet">
            A routine is an ordered block of steps you run together. Add steps, one per line, with an
            optional time box like "(10m)".
          </EmptyState>
        ) : (
          items.map((routine) => {
            const due = routineDueToday(routine, nowIso);
            const total = routineMinutes(routine);
            return (
              <div key={String(routine.id)} className="lc-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-stack-sm)' }}>
                  <strong style={{ flex: 1 }}>{routine.title}</strong>
                  <Chip tone="neutral">{routine.dayType}</Chip>
                  {total > 0 ? <Chip tone="muted">{total}m</Chip> : null}
                  <Chip tone={due ? 'warn' : 'primary'}>{due ? 'Due today' : 'Done / not due'}</Chip>
                  <Button variant="primary" disabled={!due} onClick={() => void services.routines.markRoutineRan(routine.id).then(refresh)}>
                    Mark ran
                  </Button>
                </div>
                {routine.steps.length > 0 ? (
                  <ol style={{ margin: 0, paddingLeft: 20, color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>
                    {routine.steps.map((s) => (
                      <li key={s.id}>
                        {s.label}
                        {s.minutes ? ` · ${s.minutes}m` : ''}
                      </li>
                    ))}
                  </ol>
                ) : null}
              </div>
            );
          })
        )}
      </Stack>
    </Pane>
  );
}

export function RoutinesScreen() {
  return (
    <>
      <ScreenHeader title={<>Routines & Habits</>} subtitle={<>Two distinct, linkable rhythms: habits are scheduled behaviours with streaks; routines are
            ordered step blocks you run. Misses prompt a calm nudge, never shame.</>} />
      <HabitsPane />
      <RoutinesPane />
    </>
  );
}
